import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

export const useDatasetFiltering = (flattenedData, filterConfig) => {
    const { user } = useAuth();

    const filteredData = useMemo(() => {
        if (!flattenedData || flattenedData.length === 0) return [];

        // If no filters active, return all
        if (!filterConfig || !Object.keys(filterConfig).some(k => {
            if (Array.isArray(filterConfig[k])) return filterConfig[k].length > 0;
            return filterConfig[k];
        })) {
            return flattenedData;
        }

        return flattenedData.filter(row => {
            const f = filterConfig;

            // Helpers
            const checkStr = (key, filterVal) => {
                if (!filterVal) return true;
                const val = row[key];
                return val && String(val).toLowerCase().includes(filterVal.toLowerCase());
            };
            const checkMulti = (key, filterVal) => {
                if (!filterVal || filterVal.length === 0) return true;
                const rowVal = row[key];
                if (!rowVal || rowVal === '-') return false;
                return filterVal.some(opt => rowVal.toLowerCase().includes(opt.toLowerCase()));
            };
            const checkRange = (key, min, max) => {
                const val = parseFloat(row[key]);
                if (isNaN(val)) return true;
                if (min && val < parseFloat(min)) return false;
                if (max && val > parseFloat(max)) return false;
                return true;
            };
            const checkBool = (key, filterVal) => {
                if (!filterVal) return true;
                const val = row[key];
                // formatted data might have 'Yes'/'No' or boolean
                if (val === 'Yes' || val === true) return filterVal === 'yes';
                if (val === 'No' || val === false) return filterVal === 'no';
                return true;
            };

            // Mappings (Filter Key matching Header Name from useDatasetData/datasetUtils)
            if (!checkStr('DOI', f.doi)) return false;
            if (!checkStr('Polymer Name(s)', f.polymer)) return false;
            if (!checkStr('Solvent Name(s)', f.solvent)) return false;
            if (!checkStr('Collector Type', f.collectorType)) return false;
            if (!checkStr('Needle Type', f.needleType)) return false;

            if (!checkMulti('Morphology', f.morphology)) return false;
            if (!checkMulti('Instability', f.instability)) return false;

            if (!checkBool('Formation Stable', f.isFormationStable)) return false;
            if (f.polymerType && row['Polymer Type'] !== f.polymerType) return false;
            if (f.solutionPreparation && row['Solution Preparation'] !== f.solutionPreparation) return false;

            if (!checkRange('Sol. Concentration', f.concentrationMin, f.concentrationMax)) return false;
            if (!checkRange('Needle Diameter (G)', f.needleDiameterMin, f.needleDiameterMax)) return false;
            if (!checkRange('Voltage (kV)', f.voltageMin, f.voltageMax)) return false;
            if (!checkRange('Flow Rate (mL/h)', f.flowRateMin, f.flowRateMax)) return false;
            if (!checkRange('Tip Dist (cm)', f.tipDistMin, f.tipDistMax)) return false;
            if (!checkRange('Temperature (°C)', f.tempMin, f.tempMax)) return false;
            if (!checkRange('Humidity (%)', f.humidityMin, f.humidityMax)) return false;
            if (!checkRange('Fiber Diameter (nm)', f.fiberDiameterMin, f.fiberDiameterMax)) return false;
            if (!checkRange('Diameter Variation', f.variationMin, f.variationMax)) return false;
            if (!checkRange('Nanofiber Weight', f.weightMin, f.weightMax)) return false;

            // Mechanical
            if (!checkRange('Tensile Strength (MPa)', f.tensileStrengthMin, f.tensileStrengthMax)) return false;
            if (!checkRange('Modulus (MPa)', f.modulusMin, f.modulusMax)) return false;
            if (!checkRange('Elongation at Break (%)', f.elongationAtBreakMin, f.elongationAtBreakMax)) return false;
            if (!checkStr('Fracture Behaviour', f.fractureBehaviour)) return false;

            // Functional
            if (!checkRange('Surface Area (m²/g)', f.surfaceAreaMin, f.surfaceAreaMax)) return false;
            if (!checkRange('Porosity (%)', f.porosityMin, f.porosityMax)) return false;
            if (!checkRange('Permeability', f.permeabilityMin, f.permeabilityMax)) return false;
            if (!checkRange('Wettability (Contact Angle)', f.wettabilityMin, f.wettabilityMax)) return false;
            if (!checkRange('Electrical Cond.', f.electricalConductivityMin, f.electricalConductivityMax)) return false;
            if (!checkRange('Thermal Cond.', f.thermalConductivityMin, f.thermalConductivityMax)) return false;

            if (!checkStr('Status', f.status)) return false;

            if (f.onlyMyData) {
                if (!user || !user.userId) return false;
                return row._userId === user.userId;
            }

            return true;
        });
    }, [flattenedData, filterConfig, user]);

    return filteredData;
};
